import React, { useState } from "react";
import { BASE_URL } from "../../constant/constants";

// Helper to construct full URL securely combining BASE_URL with paths
const getFullUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  const base = BASE_URL ? (BASE_URL.endsWith("/") ? BASE_URL.slice(0, -1) : BASE_URL) : "";
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${base}${cleanPath}`;
};

const CertificateCheckpage = () => {
  const [searchNo, setSearchNo] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [certData, setCertData] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    const query = searchNo.trim();
    if (!query) {
      setError("Please enter a valid certificate number.");
      return;
    }

    setLoading(true);
    setError(null);
    setCertData(null);

    try {
      const response = await fetch(
        `${BASE_URL}/signed_certificate/api/v1/signed_certificate/get_by_certificate_no?certificate_no=${encodeURIComponent(query)}`
      );

      if (response.ok) {
        const data = await response.json();
        if (data && data.certificate_file) {
          setCertData(data);
          setLoading(false);
          return;
        }
      }

      setError("No PDF found or wrong certificate number.");
    } catch (err) {
      setError("No PDF found or wrong certificate number.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="wpo-contact-pg-section section-padding py-5" style={{ backgroundColor: "#fafbfc" }}>
      {/* Dynamic inline premium styles */}
      <style>{`
        .search-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          padding: 40px;
          margin-bottom: 40px;
        }
        .premium-input {
          height: 56px;
          font-size: 16px;
          border: 1px solid #e2e8f0;
          border-radius: 6px !important;
          padding: 10px 20px;
          font-weight: 500;
          width: 100%;
          outline: none;
        }
        .premium-input:focus {
          border-color: #1565C0;
        }
        .premium-btn {
          height: 56px;
          background: #29395b;
          color: #ffffff;
          font-size: 16px;
          font-weight: bold;
          border: none;
          border-radius: 6px;
          cursor: pointer;
        }
        .premium-btn:hover {
          background: #192847;
        }
        .skeleton {
          background: #e2e8f0;
        }
      `}</style>

      <div className="container">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-9 text-center mb-5">
            <h2 className="font-weight-bold" style={{ color: "#0f172a", fontSize: "36px" }}>
              Welder Qualification Search
            </h2>
            <p className="text-muted" style={{ fontSize: "16px", maxWidth: "600px", margin: "10px auto 0" }}>
              Enter the Certificate Number below to verify and view the credentials.
            </p>
          </div>

          <div className="col-12 col-lg-8">
            {/* Search Input Box */}
            <div className="search-card">
              <form onSubmit={handleSearch}>
                <div className="row align-items-center">
                  <div className="col-12 col-md-8 mb-3 mb-md-0">
                    <input
                      type="text"
                      className="premium-input"
                      placeholder="e.g. WBA-240101-01"
                      value={searchNo}
                      onChange={(e) => setSearchNo(e.target.value)}
                    />
                  </div>
                  <div className="col-12 col-md-4">
                    <button type="submit" className="premium-btn w-100" disabled={loading}>
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                          Verifying...
                        </>
                      ) : (
                        "Verify"
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>

            {/* Error Message */}
            {error && (
              <div
                className="alert alert-danger border-0 p-4 d-flex align-items-center mb-5"
                style={{ backgroundColor: "#FFF5F5", color: "#C53030", borderLeft: "4px solid #FC8181" }}
              >
                <div className="mr-3">
                  <i className="fi flaticon-warning" style={{ fontSize: "30px" }}></i>
                </div>
                <div>
                  <h6 className="font-weight-bold mb-1">Verification Failed</h6>
                  <p className="mb-0 text-sm" style={{ opacity: 0.9 }}>{error}</p>
                </div>
              </div>
            )}

            {/* Loading Skeleton */}
            {loading && (
              <div className="card border-0" style={{ borderRadius: "6px" }}>
                <div className="p-4">
                  <div className="row align-items-center mb-4">
                    <div className="col-auto">
                      <div className="skeleton" style={{ width: "80px", height: "80px" }}></div>
                    </div>
                    <div className="col">
                      <div className="skeleton mb-2" style={{ height: "24px", width: "60%" }}></div>
                      <div className="skeleton" style={{ height: "20px", width: "40%" }}></div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-6 mb-3"><div className="skeleton" style={{ height: "50px" }}></div></div>
                    <div className="col-6 mb-3"><div className="skeleton" style={{ height: "50px" }}></div></div>
                    <div className="col-6"><div className="skeleton" style={{ height: "50px" }}></div></div>
                    <div className="col-6"><div className="skeleton" style={{ height: "50px" }}></div></div>
                  </div>
                </div>
              </div>
            )}

            {/* Result Display: SIGNED PDF CERTIFICATE */}
            {!loading && certData && (
              <div className="mb-5">
                {/* Green banner: Verified Certificate */}
                <div
                  className="d-flex align-items-center justify-content-center p-3 mb-4"
                  style={{
                    backgroundColor: "#e2f0d9",
                    border: "1px solid #b4dbb6",
                    color: "#385723",
                    fontSize: "18px",
                    fontWeight: "bold",
                    borderRadius: "6px",
                  }}
                >
                  <span style={{ marginRight: "8px" }}>✔</span> Verified Certificate
                </div>

                {/* Dark grey PDF container frame */}
                <div
                  style={{
                    backgroundColor: "#333333",
                    border: "1px solid #222222",
                    padding: "24px",
                    borderRadius: "6px",
                    marginBottom: "24px",
                  }}
                >
                  {certData.certificate_file ? (
                    <iframe
                      src={getFullUrl(certData.certificate_file)}
                      width="100%"
                      height="800px"
                      style={{ border: "none", backgroundColor: "#ffffff" }}
                      title="Certificate PDF Viewer"
                    />
                  ) : (
                    <div className="text-white text-center py-5">
                      No document file has been uploaded for this certificate record yet.
                    </div>
                  )}
                </div>

                {/* Bright blue Download PDF button */}
                {certData.certificate_file && (
                  <div className="text-center">
                    <a
                      href={getFullUrl(certData.certificate_file)}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      className="btn btn-primary px-5 py-3 text-decoration-none"
                      style={{
                        backgroundColor: "#007bff",
                        borderColor: "#007bff",
                        color: "#ffffff",
                        fontWeight: "bold",
                        fontSize: "16px",
                        borderRadius: "6px",
                        display: "inline-block",
                        border: "none",
                      }}
                    >
                      Download PDF
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificateCheckpage;
